import { FC, Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Box } from '@chakra-ui/react'
import { PointCloudOctree } from '@datalabs-jp/three-loader'
import LoadingPage from '@editor/components/LoadingPage'
import { EDITOR_POINT_SIZE_INTERVAL, EDITOR_REQUIRED_ANCHORS, EDITOR_TOOL_KEYS } from '@editor/config/constants'
import { CursorState } from '@editor/config/constants/cursor'
import { EditorContext } from '@editor/contexts/Editor'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { CanvasConfig, CanvasConfigObjects } from '@editor/interfaces/editor'
import { FileProcessingStatus } from '@editor/interfaces/file'
import usePCDLoader from '@editor/pages/projects/hooks/usePCDLoader'
import { getCenterAndRadius } from '@editor/services/Editor'
import { store, useAppDispatch, useAppSelector } from '@editor/store/app'
import { MeshState } from '@infraloop/common'
import { useContextBridge } from '@react-three/drei'
import { Canvas } from '@react-three/fiber'
import { uniqueId } from 'lodash'
import { Provider } from 'react-redux'
import { useLocation, useParams } from 'react-router'
import { Cache, LinearSRGBColorSpace, NoToneMapping, PerspectiveCamera, Vector3 } from 'three'

import RectangleMesh from '../blueprint-editor/objects/RectangleMesh'
import PotreePCD from '../common/PotreePCD'
import SolidMeshManager from '../common/SolidMeshManager'
import { setProgress, setSolidMeshStage } from '../common/SolidMeshManager/store'
import useCanvasEvents from './hooks/useCanvasEvents'
import useDocumentEventListeners from './hooks/useDocumentEventListeners'
import { CuboidAnchorFrames } from './objects/AnchorFrames'
import { BoxBoundaryMesh } from './objects/BoxBoundary'
import CameraManager from './objects/CameraManager'
import Comments from './objects/Comments/Comments'
import CuboidFrame from './objects/CuboidFrame'
import { CylinderMesh } from './objects/CylinderMesh'
import CircleAnchor from './objects/EditorCircleAnchor'
import Label from './objects/Label'
import PointCloud from './objects/PointCloud'
import { PolygonPlaneMesh } from './objects/PolygonPlaneMesh'
import PolygonPlaneMeshTransformable from './objects/PolygonPlaneMeshV2/PolygonMeshTransformable'
import { PolygonPlaneMeshV2 } from './objects/PolygonPlaneMeshV2/PolygonPlaneMeshV2'
import Polyline from './objects/Polyline'
import { setPcdDetails } from './store/editor'
import { setWorkingMask } from './tools/MaskPCD/store'
import { Tools } from './tools/setup'

Cache.enabled = true

const MainCanvas: FC<{
  windowSize: {
    width: number
    height: number
  }
}> = ({ windowSize }) => {
  // URL Params
  const { project_id } = useParams<{ project_id: string }>()
  const location = useLocation()
  const queries = new URLSearchParams(location.search)
  const inspection_area_id = queries.get('area')

  // Context
  const {
    sceneRef,
    cameraRef,

    selectionBoundaryBoxVectors,
    initCompleted: editorInitCompleted,
  } = useContext(EditorContext)

  // Store
  const dispatch = useAppDispatch()
  const userLoaded = useAppSelector((state) => state.user.userLoaded)
  const isPageLoading = useAppSelector((state) => state.page.isLoading)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)

  const cursor = useAppSelector((state) => state.toolUtil.core.cursor)
  const defaultCursor = useAppSelector((state) => state.toolUtil.core.defaultCursor)

  const workingMaskPoints = useAppSelector((state) => state.maskPCD.workingMaskCuboid)

  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const isPointCloudVisible = useAppSelector((state) => state.editor.isPcdVisible)
  const pcdBoundingSphereRadius = useAppSelector((state) => state.editor.pcdBoundingSphereRadius)

  const backgroundColor = useAppSelector((state) => state.editorSettings.display.general.bg_color)
  const pointSize = useAppSelector((state) => state.editorSettings.display.pcd.point_size)

  // States
  const [pointClouds, setPointClouds] = useState<PointCloudOctree[]>([])
  const [initCompleted, setInitCompleted] = useState(false)

  // Tools activation by running hook
  const toolsHook = useRef<
    {
      key: string
      config: CanvasConfig
    }[]
  >([])

  toolsHook.current = Tools.map((tool) => {
    const tools: {
      key: string
      config: CanvasConfig
    }[] = []
    if (tool.hooks?.useMainCanvas) {
      tools.push({
        key: tool.key,
        config: tool.hooks?.useMainCanvas({ pointClouds }),
      })
    }

    if (tool.toolbar?.variants) {
      tool.toolbar.variants.forEach((variant) => {
        if (variant.hooks?.useMainCanvas) {
          tools.push({
            key: `${tool.key}-${variant.key}`,
            config: variant.hooks.useMainCanvas({ pointClouds }),
          })
        }
      })
    }

    return tools
  })
    .flat()
    .filter(Boolean)

  const toolsObjects: {
    key: string
    objects: CanvasConfigObjects | undefined
  }[] = toolsHook.current
    .map((tool) => ({
      key: tool.key,
      objects: tool?.config.objects,
    }))
    .filter((obj) => obj.objects)

  // Hooks
  const { potree, loadPCD } = usePCDLoader()
  const { onTouchStart, onTouchMoveCapture, onTouchEnd, onMouseUp, onMouseDown, onMouseMove } = useCanvasEvents(
    pointClouds,
    toolsHook,
  )
  useDocumentEventListeners(toolsHook)

  useEffect(() => {
    pointClouds.forEach((pointCloud) => {
      if (pointCloud instanceof PointCloudOctree) {
        pointCloud.material.size = pointSize || EDITOR_POINT_SIZE_INTERVAL
      }
    })
  }, [pointSize, pointClouds])

  useEffect(() => {
    if (initCompleted && cameraRef.current) cameraRef.current.up = new Vector3(0, 0, 1)
  }, [initCompleted, cameraRef])

  /**
   * If area has already been set and inspection_area_id is different, reset store/states
   */
  useEffect(() => {
    if (initCompleted && inspection_area_id && inspectionArea?.inspection_area_id !== inspection_area_id) {
      setPointClouds([])
      setInitCompleted(false)
      dispatch(setSolidMeshStage(MeshState.None))
      dispatch(setProgress(undefined))
    }
  }, [initCompleted, inspection_area_id, inspectionArea?.inspection_area_id, dispatch])

  /**
   * Load point cloud data  when inspection area is ready
   */
  useEffect(() => {
    if (!userLoaded && !initCompleted) return

    void (async () => {
      if (
        inspection_area_id &&
        project_id &&
        inspectionArea?.inspection_area_id === inspection_area_id &&
        inspectionArea.down_sampled_file?.name &&
        inspectionArea.downsample_status?.status === FileProcessingStatus.SUCCEEDED &&
        !pointClouds.length
      ) {
        const pcd = await loadPCD({
          inspectionArea,
        })

        if (pcd instanceof PointCloudOctree) {
          // Figure out PCD dimensions
          const { bbox, center, radius } = getCenterAndRadius(pcd)
          dispatch(
            setPcdDetails({
              pcdBoundingBox: [bbox.min.toArray(), bbox.max.toArray()],
              pcdBoundingSphereRadius: radius,
              pcdBoundingBoxCenter: center.toArray(),
            }),
          )

          // Update states
          setInitCompleted(true)
          setPointClouds([pcd])
        }
      }
    })()
  }, [initCompleted, inspectionArea, pointClouds, project_id, inspection_area_id, userLoaded, loadPCD, dispatch])

  //* canvas内でcontextを使用するための設定
  const ContextBridge = useContextBridge(UserContext, EditorContext, GlobalModalContext)

  /**
   * Define cursor class for canvas
   */
  const cursorClass = useMemo(() => {
    const currentEditorTool = Tools.find((tool) => selectedTool === tool.key)
    const cursors = []

    if (currentEditorTool?.cursor) {
      cursors.push(`tooling-${currentEditorTool.cursor}`)
    }

    if (defaultCursor) {
      cursors.push(`tooling-${defaultCursor}`)
    }

    if (cursor === CursorState.NONE) {
      return cursors.join(' ')
    }

    return cursors.concat(`tooling-${cursor}`).join(' ')
  }, [cursor, defaultCursor, selectedTool])

  // -- Initial loading state
  if (!initCompleted || !editorInitCompleted || isPageLoading) {
    return <LoadingPage text="読み込み中..." />
  }

  return (
    <Box
      h="100%"
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onTouchMove={(e) => onMouseMove(undefined, e)}
      onMouseUp={onMouseUp}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchMoveCapture={onTouchMoveCapture}
      userSelect="none"
      tabIndex={-1}
    >
      <Canvas
        className={`editor-main-canvas ${cursorClass}`}
        style={{ width: '100svw', height: '100%', zIndex: 0, position: 'absolute', top: 0, left: 0 }}
        gl={{
          preserveDrawingBuffer: true,
          localClippingEnabled: false,
          toneMapping: NoToneMapping,
          outputColorSpace: LinearSRGBColorSpace,
        }}
        tabIndex={-1}
        onCreated={({ scene, camera }) => {
          sceneRef.current = scene
          if (camera instanceof PerspectiveCamera) cameraRef.current = camera
        }}
      >
        <ContextBridge>
          <Provider store={store}>
            <PotreePCD pointClouds={pointClouds} potree={potree} isPointCloudInvisible={!isPointCloudVisible} />
            <SolidMeshManager />

            <CameraManager windowSize={windowSize} />

            {/* Working cuboid */}
            {(!workingMaskPoints || workingMaskPoints.points.length < EDITOR_REQUIRED_ANCHORS.cuboid) &&
              Tools.find((tool) => selectedTool === tool.key)?.config?.additionalToolbars?.cuboidControls && (
                <CuboidAnchorFrames
                  anchor={workingMaskPoints}
                  shapeKey={EDITOR_TOOL_KEYS[selectedTool]}
                  baseDiameter={0}
                />
              )}

            <CuboidFrame
              id="working-cuboid"
              cuboidAnchor={workingMaskPoints}
              baseDiameter={0}
              maxSize={pcdBoundingSphereRadius * 2}
              onChange={(cuboid) => {
                document.dispatchEvent(new CustomEvent('editor:working-cuboid:change', { detail: cuboid }))
              }}
              onChanged={(cuboid) => {
                dispatch(setWorkingMask({ region_id: crypto.randomUUID(), ...cuboid }))
              }}
            />

            {/* Editor tools objects */}
            {toolsObjects.map((tool) => (
              <Fragment key={`tool-objects-${tool.key}`}>
                {tool.objects?.polylines?.map((props) => (
                  <Polyline key={`distance-label-single-v2-object-${props.id}`} {...props} />
                ))}
                {tool.objects?.labels?.map(({ id, ...warningLabel }) => (
                  <Label key={`label-object-${id}`} id={id} {...warningLabel} />
                ))}
                {tool.objects?.circleAnchors?.map(({ id, ...props }) => (
                  <CircleAnchor key={`circle-anchor-object-${id}`} id={id} {...props} />
                ))}
                {tool.objects?.polygonPlaneMeshes?.map((props) => (
                  <PolygonPlaneMesh key={`polygon-object-${props.polygon.shape_id}`} {...props} />
                ))}
                {tool.objects?.polygonPlaneMeshesV2?.map((props) => (
                  <PolygonPlaneMeshV2 key={`polygon-object-v2-${props.polygon.shape_id}`} {...props} />
                ))}
                {tool.objects?.rectangleMeshes?.map((props) => (
                  <RectangleMesh key={`rectangle-object-${props.id}`} {...props} />
                ))}
                {tool.objects?.polygonPlaneMeshTransformable?.map((props) => (
                  <PolygonPlaneMeshTransformable key={`polygon-object-${props.polygon.shape_id}`} {...props} />
                ))}
                {tool.objects?.cylinderMeshes?.map((props) => (
                  <CylinderMesh key={`cylinder-object-${props.cylinder.shape_id}`} {...props} />
                ))}
                {tool.objects?.pointClouds?.map((props) => (
                  <PointCloud key={`point-cloud-object-${props.id}`} {...props} />
                ))}
              </Fragment>
            ))}

            <Comments />

            {selectionBoundaryBoxVectors &&
              selectionBoundaryBoxVectors.map((vectors) => (
                <BoxBoundaryMesh key={uniqueId('boxboundary')} points={vectors} />
              ))}
          </Provider>
        </ContextBridge>

        <color attach="background" args={[backgroundColor]} />
        <ambientLight intensity={Math.PI} />
      </Canvas>
    </Box>
  )
}

export default MainCanvas
